import React, { useState, useEffect, useMemo, useRef } from 'react'
import classNames from 'classnames'
import { isMatch } from 'lodash'
import messages from 'utils/messages'
import { ChevronDown, ChevronUp } from 'react-feather'
import { Scrollbars } from 'react-custom-scrollbars'
import { FormattedMessage } from 'react-intl'
import { CloseIcon, SearchIcon, ChartSelect } from '../../../../../../images/common'
import { VuiPopOver } from '@topkamera/viso-ui'

const SelectMetricDataMenu = ({
  options = [],
  validTableName = '',
  onOptionSelected = (val) => null,
  onClose = (val) => null,
  onSearch = (val) => null,
  isOpen,
  anchorRef,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const searchInputRef = useRef(null);

  const metricOptions = useMemo(() => {
    if (options && !options.length) return [];

    const search = searchValue.trim().toLowerCase();
    const filteredOptions = options
      .filter(data => data.tableName !== 'net')
      .map(data => ({
        tableName: data.tableName,
        metricOptions: data.metrics
          .filter(metric => !search || metric.name.toLowerCase().includes(search))
          .map(metric => ({
            label: metric.name,
            value: metric.name,
            tableName: data.tableName,
            isDisabled: !!(validTableName && validTableName !== data.tableName)
          }))
      }))
      .filter(data => data.metricOptions.length > 0); // Only show tables with matching metrics

    return filteredOptions;
  }, [options, validTableName, searchValue]);

  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  const handleOnOptionSelected = (optionSelected) => {
    if (!optionSelected.isDisabled && (!validTableName || validTableName === optionSelected.tableName)) {
      onOptionSelected(optionSelected)
    }
  }

  const handleSearch = (value) => {
    setSearchValue(value)
    onSearch && onSearch(value)
  }

  return (
    <VuiPopOver
      onClose={onClose}
      elementId='popoverRoot'
      isOpen={isOpen}
      anchorRef={anchorRef}
      position='top'
      offset={2}
      fullWidth={{
        isFullWidth: true
      }}
      animation='roll'
    >
      <div
        className='chart-editor-multiselect-metric-data-menu'
        onClick={event => event.stopPropagation()}
        style={{ overscrollBehavior: 'contain' }}
      >
        <div className='text-field text-field_nolabel text-field--search'>
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search"
            className="text-field__input text-field__input--search"
            onChange={(e) => handleSearch(e.target.value)}
            value={searchValue}
          />
          <div className="text-field__icon">{!searchValue && <SearchIcon />}</div>
          {searchValue && <span className="cursor text-field__icon text-field__icon_clickable" onClick={() => handleSearch('')}><CloseIcon /></span>}
        </div>
        <Scrollbars
          autoHeight
          className="custom-scrollbars"
          autoHeightMax={214}
          universal
          renderThumbVertical={props => <div {...props} className="thumb-vertical" />}
          renderView={props => (
            <div
              {...props}
              style={{ ...props.style, overscrollBehavior: 'contain' }}
            />
          )}
        >
          {metricOptions.length === 0 ?
            (
              <div className='chart-editor-multiselect-metric-data-menu__option'>
                <FormattedMessage {...messages.noDataAvailable} />
              </div>
            )
            :
            metricOptions.map((table, index) => {
              return (
                <React.Fragment key={table.tableName}>

                  <div className='chart-editor-multiselect-metric-data-menu__header'>
                    {`TableName ${table.tableName}`.toUpperCase()}
                  </div>

                  {table.metricOptions.map(metricOption => {
                    return (
                      <div
                        key={metricOption.value}
                        className={classNames('chart-editor-multiselect-metric-data-menu__option', {
                          'chart-editor-multiselect-metric-data-menu__option__disabled': metricOption.isDisabled
                        })}
                        onClick={() => handleOnOptionSelected(metricOption)}
                      >
                        <div className='chart-editor-multiselect-metric-data-menu__option__icon'>
                          <ChartSelect />
                        </div>
                        <div>
                          {metricOption.label}
                        </div>
                      </div>
                    )
                  })}

                  {(metricOptions.length - 1 > index) &&
                    <div className='chart-editor-multiselect-metric-data-menu__separator' />
                  }
                </React.Fragment>
              )
            })
          }
        </Scrollbars>
      </div>
    </VuiPopOver>
  )
}

const SelectSimpleMenu = ({
  options = [],
  withDashboardDefaultOption = false,
  onOptionSelected = (val) => null,
  onClose = (val) => null,
  isOpen,
  anchorRef,
}) => {

  const simpleOptions = useMemo(() => {
    if (options && !options.length) return []

    const opts = options.map(option => ({
      label: option,
      value: option
    }))

    if (withDashboardDefaultOption) {
      opts.unshift({ label: 'Dashboard default', value: 'dashboard default' })
    }

    return opts
  }, [options])

  const handleOnOptionSelected = (optionSelected) => {
    onOptionSelected(optionSelected)
  }

  return (
    <VuiPopOver
      onClose={onClose}
      elementId='popoverRoot'
      isOpen={isOpen}
      anchorRef={anchorRef}
      offset={0}
      position='top'
      fullWidth={{
        isFullWidth: true
      }}
      animation='roll'
    >
      <div
        className='chart-editor-multiselect-simple-menu'
        onClick={event => event.stopPropagation()}
        style={{ overscrollBehavior: 'contain' }}
      >
        <Scrollbars
          autoHeight
          className="custom-scrollbars"
          autoHeightMax={214}
          universal
          renderThumbVertical={props => <div {...props} className="thumb-vertical" />}
        >
          {(simpleOptions && simpleOptions.length > 0)
            ?
            (simpleOptions.map((option, index) => (
              <div
                className='chart-editor-multiselect-simple-menu__option'
                key={index}
                onClick={() => handleOnOptionSelected(option)}
              >
                {option.label}
              </div>
            )))
            :
            (
              <div className='chart-editor-multiselect-simple-menu__option'>
                <FormattedMessage {...messages.noDataAvailable} />
              </div>
            )
          }
        </Scrollbars>
      </div>
    </VuiPopOver>
  )
}

export const MultiSelect = ({
  label = '',
  placeholder = '',
  selectedOptions = [],
  options = [],
  validTableName = '',
  isSearchable = false,
  onChange = (val) => null,
  withSimpleMenu = false,
  withMetricDataMenu = false,
  showSelectedOptions = true,
  withDashboardDefaultOption = false,
  defaultSelection = '',
  isSingleSelect = false
}) => {

  const [currentSelectedOptions, setCurrentSelectedOptions] = useState(selectedOptions)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const anchorRef = useRef(null)

  useEffect(() => {
    if (!areArraysEqualWithSubsetCheck(selectedOptions, currentSelectedOptions)) {
      setCurrentSelectedOptions(selectedOptions)
    }
  }, [selectedOptions])

  const handleOnSelectedOption = (selectedOption) => {
    if (selectedOption.value === 'dashboard default') {
      onChange([])
      return
    }

    const optionIndex = currentSelectedOptions.findIndex(option => option.value === selectedOption.value)

    if (optionIndex === -1) {
      const newSelectedOptions = [...currentSelectedOptions, selectedOption.value]
      onChange(newSelectedOptions)
    } else {
      const newSelectedOptions = currentSelectedOptions.filter((_, index) => index !== optionIndex)
      onChange(newSelectedOptions)
    }
  }

  const handleOnMetricDataSelectedOption = (selectedMetric) => {
    const metricIndex = currentSelectedOptions
      .findIndex(metric =>
        metric.value === selectedMetric.value && metric.tableName === selectedMetric.tableName
      )
    if (metricIndex === -1) {
      const newSelectedOptions = [...currentSelectedOptions, selectedMetric]

      if (isSingleSelect) {
        return onChange(newSelectedOptions.filter((item, index) => index === newSelectedOptions.length - 1))
      }

      onChange(newSelectedOptions)
      // setCurrentSelectedOptions(() => {
      //   return newSelectedOptions
      // })

    } else {
      const newSelectedOptions = currentSelectedOptions.filter((_, index) => index !== metricIndex)

      if (isSingleSelect) {
        return onChange(newSelectedOptions.filter((item, index) => index === newSelectedOptions.length - 1))
      }

      onChange(newSelectedOptions)
      // setCurrentSelectedOptions(() => {
      //   return newSelectedOptions
      // })
    }
  }

  const removeSelectedOption = (optionIndex) => {
    // if (currentSelectedOptions[optionIndex].value !== defaultSelection) {
    const newSelectedOptions = currentSelectedOptions.filter((_, index) => index !== optionIndex)
    onChange(newSelectedOptions)
    // }

  }

  const handleToggleMenu = () => {
    setIsMenuOpen(prevState => !prevState)
  }

  return (
    <div className='chart-editor-multiselect'>
      {label &&
        <div className='chart-editor__label'>
          <label className="text-field__label1">{label}</label>
        </div>
      }
      <div
        className='chart-editor-multiselect-input'
        onClick={handleToggleMenu}
        ref={anchorRef}
      >
        <div className='chart-editor-multiselect-input__box'>
          {currentSelectedOptions.length > 0
            ?
            showSelectedOptions
              ? currentSelectedOptions.map((option, index) => {
                const optionLabel = option?.label ? option.label : option
                const labelStr = typeof optionLabel === 'string' ? optionLabel : String(optionLabel)
                const displayLabel = labelStr.length > 25 ? `${labelStr.slice(0, 25)}…` : labelStr
                return (
                  <div
                    key={index}
                    className="chart-editor-multiselect-input__box__selected-option"
                    title={labelStr}
                    aria-label={labelStr}
                    onClick={event => {
                      event.stopPropagation()
                      removeSelectedOption(index)
                    }}
                  >
                    {displayLabel}
                  </div>
                )
              })
              : <div> {`${currentSelectedOptions.length} selected`} </div>
            : placeholder
              ? <div className="chart-editor-multiselect-input__box__placeholder" >
                {placeholder}
              </div>
              : null
          }
        </div>
        <div className='chart-editor-multiselect-input__icon'>
          {isMenuOpen ? <ChevronUp /> : <ChevronDown />}
        </div>

        {withSimpleMenu &&
          <SelectSimpleMenu
            options={options}
            withDashboardDefaultOption={withDashboardDefaultOption}
            onOptionSelected={val => {
              handleOnSelectedOption(val)
              handleToggleMenu()
            }}
            anchorRef={anchorRef}
            isOpen={isMenuOpen}
            onClose={handleToggleMenu}
          />
        }

        {withMetricDataMenu &&
          <SelectMetricDataMenu
            options={options}
            isSingleSelect={isSingleSelect}
            validTableName={validTableName}
            onOptionSelected={val => {
              handleOnMetricDataSelectedOption(val)
              handleToggleMenu()
            }}
            anchorRef={anchorRef}
            isOpen={isMenuOpen}
            onClose={handleToggleMenu}
          />
        }
      </div>
    </div>
  )
}

function areArraysEqualWithSubsetCheck(arr1, arr2) {
  if (!Array.isArray(arr1) || !Array.isArray(arr2)) return false
  if (arr1.length !== arr2.length) return false

  const arr2Copy = [...arr2]

  return arr1.every(obj1 => {
    const matchIndex = arr2Copy.findIndex(obj2 => isMatch(obj1, obj2) || isMatch(obj2, obj1))
    if (matchIndex === -1) {
      return false
    }
    arr2Copy.splice(matchIndex, 1)
    return true
  })
}